import HierarchicalSearchSelect from "../HierarchicalSearchSelect";
import { Controller } from "react-hook-form";
import React from "react";
import StyledSelect from "../StyledSelect";
import StyledSearchSelect from "../StyledSearchSelect";

const SchoolCompanySelector = ({
  role,
  isOwner,
  isPrimaryAdmin,
  control,
  lockedSchools,
  companies,
  isEditingSelf,
  errors,
  schoolOptions,
}) => {
  if (role === "company_owner") {
    return (
      <CompanySelector
        companies={companies}
        control={control}
        errors={errors}
        isEditingSelf={isEditingSelf}
        isPrimaryAdmin={isPrimaryAdmin}
        lockedSchools={lockedSchools}
        role={role}
        schoolOptions={schoolOptions}
      />
    );
  }

  if (
    !["school_admin", "sub_admin"].includes(role) &&
    (isOwner || role === "super_admin")
  ) {
    return (
      <SchoolAndCompanySelector
        companies={companies}
        control={control}
        errors={errors}
        isEditingSelf={isEditingSelf}
        isOwner={isOwner}
        lockedSchools={lockedSchools}
        role={role}
      />
    );
  }

  return (
    <SchoolSelector
      control={control}
      errors={errors}
      isEditingSelf={isEditingSelf}
      lockedSchools={lockedSchools}
      isPrimaryAdmin={isPrimaryAdmin}
      role={role}
      schoolOptions={schoolOptions}
    />
  );
};

const SchoolAndCompanySelector = ({
  role,
  control,
  lockedSchools,
  companies,
  isOwner,
  isEditingSelf,
  errors,
}) => {
  const schoolAdminSelected = role === "school_admin";
  const subAdminSelected = role === "sub_admin";

  return (
    <Controller
      control={control}
      name="schools"
      render={({ field }) => {
        // Ensure locked schools are always included in the value
        const ensureLockedSchools = (newValue) => {
          return [...new Set([...lockedSchools, ...newValue])];
        };

        return (
          <HierarchicalSearchSelect
            allowTopLevelSelect={
              schoolAdminSelected || subAdminSelected ? false : true
            }
            companies={companies}
            disabled={isEditingSelf}
            error={!!errors.schools}
            helperText={
              errors.schools?.message ||
              (lockedSchools.length > 0
                ? `${lockedSchools.length} school(s) are locked and cannot be unselected`
                : undefined)
            }
            label={
              isOwner
                ? schoolAdminSelected
                  ? "School"
                  : "Companies & Schools"
                : "Schools"
            }
            lockedSchools={lockedSchools}
            onChange={(newValue) => {
              const finalValue = ensureLockedSchools(newValue);
              field.onChange(finalValue);
            }}
            placeholder={
              schoolAdminSelected ? "Select School" : "Select companies/schools"
            }
            singleCompanySelect={
              schoolAdminSelected || subAdminSelected ? true : false
            }
            value={ensureLockedSchools(field.value || [])}
          />
        );
      }}
      rules={{
        required:
          role !== "product_owner"
            ? "At least one company or school is required"
            : undefined,
        validate: (value) => {
          if (lockedSchools.length > 0) {
            const hasAllLockedSchools = lockedSchools.every((id) =>
              value.includes(id)
            );
            if (!hasAllLockedSchools) {
              return "Cannot unselect locked schools";
            }
          }

          if (role === "school_admin" || role === "sub_admin") {
            if (value.length !== 1) {
              return "Must select exactly one school for this role";
            }
            if (
              lockedSchools.length === 1 &&
              !value.includes(lockedSchools[0])
            ) {
              return "Must select the locked school";
            }
          }

          return true;
        },
      }}
    />
  );
};

const SchoolSelector = ({
  role,
  control,
  isEditingSelf,
  isPrimaryAdmin,
  schoolOptions,
  errors,
}) => {
  const sortedSchoolOptions = schoolOptions?.sort((a, b) =>
    a.label.localeCompare(b.label)
  );
  return (
    <Controller
      control={control}
      name="schools"
      render={({ field }) => {
        // Find the selected school option based on the numeric ID
        const selectedSchool = sortedSchoolOptions?.find(
          (option) => option.value === field.value?.[0]
        );

        return (
          <StyledSearchSelect
            {...field}
            disabled={!role || isEditingSelf || isPrimaryAdmin}
            error={!!errors.schools}
            helperText={errors.schools?.message}
            label="School"
            onChange={(selectedOption) => {
              // Convert the selected option back to array format
              field.onChange(selectedOption ? [selectedOption.value] : []);
            }}
            options={sortedSchoolOptions || []}
            placeholder="Select school"
            value={selectedSchool || null}
          />
        );
      }}
      rules={{ required: "At least one school is required" }}
    />
  );
};

const CompanySelector = ({
  role,
  control,
  isEditingSelf,
  isPrimaryAdmin,
  lockedSchools,
  errors,
  companies,
}) => {
  return (
    <Controller
      control={control}
      name="schools"
      render={({ field }) => {
        return (
          <StyledSelect
            disabled={!role || isEditingSelf || isPrimaryAdmin}
            error={!!errors.schools}
            helperText={errors.schools?.message}
            label="Company"
            onChange={(e) => field.onChange([e.target.value])}
            options={
              companies?.map((company) => {
                return {
                  value: company.id,
                  label: company.name,
                  disabled: lockedSchools.includes(company.id),
                  selected: lockedSchools.includes(company.id),
                };
              }) || []
            }
            placeholder="Select company"
            value={field.value?.[0] || ""}
          />
        );
      }}
      rules={{ required: "At least one company is required" }}
    />
  );
};

export default SchoolCompanySelector;
